/**
 * `canary local init` — Interactive wizard for setting up local testing.
 *
 * Walks through: property selection, local URL, credential setup.
 *
 * @module
 */

import process from 'node:process';
import { getArgValue, resolveConfig } from '../auth.js';
import {
  selectProperty,
  promptInput,
  promptChoice,
  apiRequest,
  uploadStorageState,
} from '../cli-helpers.js';
import {
  createSession,
  deleteSession,
  getSessionStorageState,
} from '../session/daemon-client.js';

export async function runLocalInit(argv: string[]) {
  const config = await resolveConfig(argv);
  const propertyHint = getArgValue(argv, '--property');
  const urlFlag = getArgValue(argv, '--url');
  const credentialsFlag = getArgValue(argv, '--credentials');
  const usernameFlag = getArgValue(argv, '--username');
  const passwordFlag = getArgValue(argv, '--password');
  const loginUrlFlag = getArgValue(argv, '--login-url');
  const loginInstructionsFlag = getArgValue(argv, '--login-instructions');

  // 1. Select property (already respects --property)
  console.log('\n🔧 Local Testing Setup\n');
  const property = await selectProperty(config.apiUrl, config.token, propertyHint);
  if (!property) {
    process.exitCode = 1;
    return;
  }

  // 2. Local URL
  const localUrl =
    urlFlag ?? (await promptInput('What is the local testing URL?', 'http://localhost:3000'));
  if (!localUrl) {
    console.error('URL is required. Pass --url to skip the prompt.');
    process.exitCode = 1;
    return;
  }

  // 3. Create local environment
  const envName = `Local - ${localUrl}`;
  const envResult = await apiRequest<{
    ok: boolean;
    item?: { id: string; name: string };
    error?: string;
  }>(config.apiUrl, config.token, 'POST', `/org/properties/${property.id}/environments`, {
    name: envName,
    url: localUrl,
    type: 'local',
  });

  if (!envResult.ok || !envResult.item) {
    console.error(`Failed to create environment: ${envResult.error ?? 'Unknown error'}`);
    process.exitCode = 1;
    return;
  }

  console.log(`Created local environment: ${envResult.item.name}`);

  // 4. Credential setup
  let credentialChoice: 'enter' | 'manual' | 'skip' | null;
  if (credentialsFlag) {
    if (credentialsFlag !== 'enter' && credentialsFlag !== 'manual' && credentialsFlag !== 'skip') {
      console.error(`--credentials must be one of: enter, manual, skip (got "${credentialsFlag}")`);
      process.exitCode = 1;
      return;
    }
    credentialChoice = credentialsFlag;
  } else {
    credentialChoice = await promptChoice<'enter' | 'manual' | 'skip'>(
      'Does this app need credentials?',
      [
        { label: "Yes, I'll enter username & password", value: 'enter' },
        { label: "No credentials, I'll log in myself", value: 'manual' },
        { label: 'Skip — no login needed', value: 'skip' },
      ]
    );
  }

  if (credentialChoice === 'enter') {
    await handleEnterCredentials(config, property.id, envResult.item.id, localUrl, {
      username: usernameFlag,
      password: passwordFlag,
      loginUrl: loginUrlFlag,
      loginInstructions: loginInstructionsFlag,
    });
  } else if (credentialChoice === 'manual') {
    await handleManualLogin(config, property.id, envResult.item.id, localUrl);
  } else {
    console.log('\nLocal environment ready. Run `canary local start` to begin.');
  }
}

async function handleEnterCredentials(
  config: { apiUrl: string; token: string },
  propertyId: string,
  environmentId: string,
  localUrl: string,
  flags: {
    username?: string;
    password?: string;
    loginUrl?: string;
    loginInstructions?: string;
  }
) {
  const username = flags.username ?? (await promptInput('Username'));
  const password = flags.password ?? (await promptInput('Password'));

  if (!username || !password) {
    console.error(
      'Username and password are required. Pass --username and --password to skip the prompts.'
    );
    process.exitCode = 1;
    return;
  }

  let loginUrl = flags.loginUrl;
  if (!loginUrl) {
    loginUrl = await promptInput('Login page URL', `${localUrl}/login`);
  }

  let loginInstructions = flags.loginInstructions;
  if (loginInstructions === undefined) {
    console.log(
      '\nDescribe the login flow in plain language — the agent will read these instructions to'
    );
    console.log('log you back in if the session expires. Example:');
    console.log(
      '  "Fill the email field, fill the password field, click Continue. Dismiss any'
    );
    console.log("   'Stay signed in?' modal that appears.\"\n");
    loginInstructions = await promptInput(
      'Login instructions (optional, press Enter to skip)'
    );
  }

  const credResult = await apiRequest<{
    ok: boolean;
    id?: string;
    error?: string;
  }>(config.apiUrl, config.token, 'POST', `/org/properties/${propertyId}/credentials`, {
    name: `Local - ${username}`,
    environmentId,
    authProvider: 'basic',
    data: { username, password },
    loginUrl,
    loginInstructions: loginInstructions || undefined,
  });

  if (!credResult.ok) {
    console.error(`Failed to create credential: ${credResult.error ?? 'Unknown error'}`);
    process.exitCode = 1;
    return;
  }

  console.log(`\nCreated credential: Local - ${username}`);
  if (loginInstructions) {
    console.log('If this session expires, the agent can log you back in automatically.');
  }
  console.log('Run `canary local start` to begin a session.');
}

async function handleManualLogin(
  config: { apiUrl: string; token: string },
  propertyId: string,
  environmentId: string,
  localUrl: string
) {
  // Create a minimal credential
  const credResult = await apiRequest<{
    ok: boolean;
    id?: string;
    error?: string;
  }>(config.apiUrl, config.token, 'POST', `/org/properties/${propertyId}/credentials`, {
    name: 'Local - manual',
    environmentId,
    authProvider: 'custom',
    data: {},
    loginUrl: localUrl,
  });

  if (!credResult.ok || !credResult.id) {
    console.error(`Failed to create credential: ${credResult.error ?? 'Unknown error'}`);
    process.exitCode = 1;
    return;
  }

  const credentialId = credResult.id;

  // Start a headed session
  console.log('\nOpening browser...');
  const sessionResult = await createSession({
    name: 'local-login',
    url: localUrl,
  });

  if (!sessionResult.ok || !sessionResult.data) {
    console.error(`Failed to start browser session: ${sessionResult.error ?? 'Unknown error'}`);
    process.exitCode = 1;
    return;
  }

  const sessionId = sessionResult.data.id;

  console.log(`Browser opened at ${localUrl}. Log in, then press Enter when done.`);

  // Wait for Enter keypress
  const readline = await import('node:readline');
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  await new Promise<void>((resolve) => {
    rl.question('', () => {
      rl.close();
      resolve();
    });
  });

  // Extract storage state
  console.log('Extracting login state...');
  const storageResult = await getSessionStorageState(sessionId);

  if (!storageResult.ok || !storageResult.data) {
    console.error(`Failed to extract storage state: ${storageResult.error ?? 'Unknown error'}`);
    await deleteSession(sessionId);
    process.exitCode = 1;
    return;
  }

  // Upload storage state
  console.log('Saving login state...');
  const uploadResult = await uploadStorageState({
    apiUrl: config.apiUrl,
    token: config.token,
    propertyId,
    credentialId,
    storageState: storageResult.data,
  });

  if (!uploadResult.ok) {
    console.error(`Failed to save login state: ${uploadResult.error ?? 'Unknown error'}`);
    await deleteSession(sessionId);
    process.exitCode = 1;
    return;
  }

  // Clean up login session
  await deleteSession(sessionId);

  console.log('\nLogin state saved!');
  console.log(
    '\nWhen this session expires, re-run `canary local login` (you will log in by hand again).'
  );
  console.log(
    'To let the agent recover expired sessions without opening a browser, re-run'
  );
  console.log('`canary local init` with `--credentials enter` and provide username + password.');
  console.log('\nRun `canary local start` to begin.');
}
